import { reactive } from "vue";
import type { Ref } from "vue";
import {
  type ParentGroup,
  GetParentGroupQuery,
  GetParentGroupQueryVariables,
  GetParentGroupDocument,
  GetDeliveryGroupQuery,
  GetDeliveryGroupQueryVariables,
  GetDeliveryGroupDocument,
  ListDeliveryAreaQuery,
  ListDeliveryAreaQueryVariables,
  ListDeliveryAreaDocument,
  type DeliveryGroupGetInput,
  type DeliveryGroupListInput,
  type ShopListInput,
} from "@/generated/graphql";
import {
  localStorageGetCustomerCode,
} from "@/common/localStorageUtil";
import { generateApolloClient } from "@/common/apolloClientUtil";
import { getShopList } from "@/common/graphql/shop";
import { getDeliveryGroupList } from "@/common/graphql/deliveryGroup";
import type {
  DeliveryTimeConfirm,
  ChainSelectValue,
  ShopSelectValue,
  ShopItem,
  AreaListItem,
  DeliveryGroupSelectValue,
  DeliveryGroupIdItem,
  ListData,
  ListGroupData,
  DeliveryCapSettingDialogReturn,
} from "../types";

/**
 * グループ確認・配達情報確認ダイアログの操作、タブ間遷移
 */
export const useDeliveryCapSettingDialog = (params: {
  overlay: Ref<boolean>;
  model: Ref<number>;
  groupConfirmDialog: Ref<boolean>;
  deliveryTimeConfirmDialog: Ref<boolean>;
  selectShopFlg: Ref<boolean>;
  selectAreaFlg: Ref<boolean>;
  selectGroupFlg: Ref<boolean>;
  selectGroupIdFlg: Ref<boolean>;
  setIdFlg: Ref<boolean>;
  chainSelectValues: Ref<ChainSelectValue>;
  shopSelectValues: Ref<ShopSelectValue>;
  shopListItems: Ref<ShopItem[]>;
  areaListItems: Ref<(AreaListItem | null)[]>;
  areaSelectValues: Ref<AreaListItem | null>;
  deliveryGroupListItems: Ref<(import("@/generated/graphql").DeliveryGroup | null | undefined)[]>;
  deliveryGroupSelectValues: Ref<DeliveryGroupSelectValue>;
  deliveryGroupItems: Ref<DeliveryGroupIdItem[]>;
  deliveryGroupIdSelectValues: Ref<DeliveryGroupIdItem[]>;
  shopListInput: ShopListInput;
  deliveryGroupListInput: DeliveryGroupListInput;
  deliveryGroupGetInput: DeliveryGroupGetInput;
  displayDateFrom: Ref<string | null>;
  displayDateTo: Ref<string | null>;
  listDataArray: Ref<ListData[]>;
  listGroupDataArray: Ref<ListGroupData[]>;
  getCapOrders: () => Promise<void>;
  getGroupCapOrders: () => Promise<void>;
  onClickShopTab: () => Promise<void>;
  onClickGroupTab: () => Promise<void>;
}): DeliveryCapSettingDialogReturn => {
  const {
    overlay,
    model,
    groupConfirmDialog,
    deliveryTimeConfirmDialog,
    selectShopFlg,
    selectAreaFlg,
    selectGroupFlg,
    selectGroupIdFlg,
    setIdFlg,
    chainSelectValues,
    shopSelectValues,
    shopListItems,
    areaListItems,
    areaSelectValues,
    deliveryGroupListItems,
    deliveryGroupSelectValues,
    deliveryGroupItems,
    deliveryGroupIdSelectValues,
    shopListInput,
    deliveryGroupListInput,
    deliveryGroupGetInput,
    displayDateFrom,
    displayDateTo,
    listDataArray,
    listGroupDataArray,
    getCapOrders,
    getGroupCapOrders,
    onClickShopTab,
    onClickGroupTab,
  } = params;

  // 所属グループ便キャップ一覧取得 input
  const parentGroupItems = reactive<ParentGroup>({
    parent_group_response: {
      customer_code: localStorageGetCustomerCode(),
      chain_code: "",
      shop_code: "",
      shop_name: "",
      area_code: "",
      area_name: "",
      parent_group_list: [],
    },
  });

  const deliveryTimeConfirm = reactive<DeliveryTimeConfirm>({
    shop_code: "",
    shop_name: "",
    area_code: "",
    area_name: "",
    delivery_id: 0,
    delivery_time: "",
  });

  /**
   * エリアのグループ確認 @change
   */
  const confirmAreaGroup = async (area_code: string): Promise<void> => {
    console.log(area_code);

    // graphql接続
    const apolloClient = await generateApolloClient();

    const parentGroup = await apolloClient.query<
      GetParentGroupQuery,
      GetParentGroupQueryVariables
    >({
      query: GetParentGroupDocument,
      variables: {
        customer_code: localStorageGetCustomerCode(),
        chain_code: chainSelectValues.value.chain_code,
        shop_code: shopSelectValues.value.shop_code,
        area_code: area_code,
      },
      fetchPolicy: "no-cache",
    });

    if (parentGroup.data.getParentGroup?.parent_group_response) {
      parentGroupItems.parent_group_response =
        parentGroup.data.getParentGroup.parent_group_response;
    }
    console.log(parentGroupItems);

    groupConfirmDialog.value = true;
  };

  /**
   * エリア便のグループ確認 @change
   */
  const confirmDeliveryGroup = async (item: { area_code: string; delivery_id: number }): Promise<void> => {
    console.log(item.delivery_id);

    // graphql接続
    const apolloClient = await generateApolloClient();

    const parentGroup = await apolloClient.query<
      GetParentGroupQuery,
      GetParentGroupQueryVariables
    >({
      query: GetParentGroupDocument,
      variables: {
        customer_code: localStorageGetCustomerCode(),
        chain_code: chainSelectValues.value.chain_code,
        shop_code: shopSelectValues.value.shop_code,
        area_code: item.area_code,
        delivery_id_list: String(item.delivery_id),
      },
      fetchPolicy: "no-cache",
    });

    if (parentGroup.data.getParentGroup?.parent_group_response) {
      parentGroupItems.parent_group_response =
        parentGroup.data.getParentGroup.parent_group_response;
    }

    console.log(parentGroupItems);

    groupConfirmDialog.value = true;
  };

  /**
   * グループキャップへ移動 @change
   */
  const toGroupCap = async (group_code: string): Promise<void> => {
    model.value = 1;
    await onClickGroupTab();

    // 配達グループ情報を取得する
    deliveryGroupListInput.chain_code = chainSelectValues.value.chain_code;

    const response = await getDeliveryGroupList(deliveryGroupListInput);

    deliveryGroupListItems.value = (response ?? [])
      .filter((item): item is NonNullable<typeof item> => item !== null && item !== undefined)
      .map((item) => ({
        __typename: "DeliveryGroup" as const,
        chain_code: item.chain_code,
        customer_code: item.customer_code,
        group_code: item.group_code,
        name: item.name,
        DeliveryGroupShopList: item.DeliveryGroupShopList ?? [],
        result: {
          __typename: "Result" as const,
          result: "",
          message: null,
        },
      }));

    console.log("mode changed to group");

    const matchedGroup = deliveryGroupListItems.value.find(
      (item) => item?.group_code === group_code
    );
    deliveryGroupSelectValues.value = {
      group_code: group_code,
      name: matchedGroup?.name ?? "",
    };
    deliveryGroupGetInput.group_code = group_code;

    deliveryGroupIdSelectValues.value = [];
    deliveryGroupItems.value = [];
    listGroupDataArray.value = [];

    // 配達グループ便の取得
    const apolloClient = await generateApolloClient();
    const deliveryGroup = await apolloClient.query<
      GetDeliveryGroupQuery,
      GetDeliveryGroupQueryVariables
    >({
      query: GetDeliveryGroupDocument,
      variables: { input: deliveryGroupGetInput },
      fetchPolicy: "no-cache",
    });

    if (
      deliveryGroup.data.getDeliveryGroup &&
      deliveryGroup.data.getDeliveryGroup.response &&
      deliveryGroup.data.getDeliveryGroup.response.length > 0
    ) {
      deliveryGroup.data.getDeliveryGroup.response!.forEach((value) => {
        if (value) {
          deliveryGroupItems.value.push({
            group_delivery_id: String(value.group_delivery_id),
            group_delivery_id_name:
              value.group_delivery_id === 0
                ? "都度便"
                : `${value.group_delivery_id}便`,
          });
        }
      });
    }

    if (displayDateFrom.value && displayDateTo.value) {
      selectGroupFlg.value = true;
      selectGroupIdFlg.value = false;
      setIdFlg.value = true;

      deliveryGroupIdSelectValues.value = [];
      await getGroupCapOrders();
    } else {
      selectGroupFlg.value = false;
      selectGroupIdFlg.value = false;
    }

    groupConfirmDialog.value = false;
  };

  /**
   * エリア便のグループ確認 @change
   */
  const confirmDeliveryTime = (item: {
    shop_code: string;
    shop_name: string;
    area_code: string;
    area_name: string;
    delivery_id: number;
    delivery_time: number;
  }): void => {
    deliveryTimeConfirm.shop_code = item.shop_code;
    deliveryTimeConfirm.shop_name = item.shop_name;
    deliveryTimeConfirm.area_code = item.area_code;
    deliveryTimeConfirm.area_name = item.area_name;
    deliveryTimeConfirm.delivery_id = item.delivery_id;
    deliveryTimeConfirm.delivery_time = String(item.delivery_time);

    deliveryTimeConfirmDialog.value = true;
  };

  /**
   * グループキャップへ移動 @change
   */
  const toShopCap = async (): Promise<void> => {
    model.value = 0;
    await onClickShopTab();

    // 配達グループ情報を取得する
    shopListInput.chain_code = chainSelectValues.value.chain_code;
    shopListItems.value = await getShopList(shopListInput);

    shopSelectValues.value.shop_code = deliveryTimeConfirm.shop_code;
    shopSelectValues.value.name = deliveryTimeConfirm.shop_name;

    // エリアデータ取得処理
    const apolloClient = await generateApolloClient();
    const listArea = await apolloClient.query<
      ListDeliveryAreaQuery,
      ListDeliveryAreaQueryVariables
    >({
      query: ListDeliveryAreaDocument,
      variables: {
        chain_code: chainSelectValues.value.chain_code,
        customer_code: localStorageGetCustomerCode(),
        shop_code: shopSelectValues.value.shop_code,
      },
      fetchPolicy: "no-cache",
    });

    if (!listArea.data.listDeliveryArea || listArea.data.listDeliveryArea.length === 0) {
      selectShopFlg.value = false;
      selectAreaFlg.value = false;
      overlay.value = false;
      return;
    }
    areaListItems.value = listArea.data.listDeliveryArea;
    areaSelectValues.value =
      areaListItems.value.find(
        (area) => area?.area_code === deliveryTimeConfirm.area_code
      ) ?? null;
    listDataArray.value = [];

    if (displayDateFrom.value && displayDateTo.value) {
      await getCapOrders();
      selectShopFlg.value = false;
      selectAreaFlg.value = true;
    } else {
      selectShopFlg.value = false;
      selectAreaFlg.value = false;
    }

    deliveryTimeConfirmDialog.value = false;
  };

  return {
    parentGroupItems,
    deliveryTimeConfirm,
    confirmAreaGroup,
    confirmDeliveryGroup,
    toGroupCap,
    confirmDeliveryTime,
    toShopCap,
  };
};
